import React, { useCallback, useEffect, useState } from 'react';
import {
    Overlay,
    ModalContainer,
    CloseButton,
    IllustrationWrapper,
    ModalTitle,
    CheckboxWrapper,
    HiddenCheckbox,
    CustomCheckbox,
    CheckboxLabel,
    ConfirmButton,
} from './marketing-consent-modal.style';
import receiveEmailsImg from './receiveEmails.png';

const STORAGE_KEY = 'marketingConsentModalLastShown';
const SOCIAL_LOGIN_FLAG_KEY = 'socialLoginJustCompleted';
const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000;
// const ONE_MONTH_MS = 5 * 60 * 1000; // 测试临时 5min

interface MarketingConsentModalProps {
    email: string;
    isMobile: boolean;
    sellerUrl: string;
}

const MarketingConsentModal = ({ email, isMobile, sellerUrl }: MarketingConsentModalProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [optIn, setOptIn] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!localStorage.getItem(SOCIAL_LOGIN_FLAG_KEY)) return;
        localStorage.removeItem(SOCIAL_LOGIN_FLAG_KEY);

        const lastShown = localStorage.getItem(STORAGE_KEY);
        if (lastShown && Date.now() - Number(lastShown) < ONE_MONTH_MS) return;

        const checkMarketingStatus = async () => {
            try {
                const response = await fetch(
                    `/bff-api/user-marketing/status?email=${encodeURIComponent(email)}`,
                    { credentials: 'include' }
                );

                if (!response.ok) return;

                const data = await response.json();

                if (!data.hasOptInMarketing) {
                    setIsOpen(true);
                }
            } catch {
                // Silently fail
            }
        };

        checkMarketingStatus();
    }, [email]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
    }, []);

    const handleConfirm = useCallback(async () => {
        if (isSubmitting) return;

        setIsSubmitting(true);
        try {
            const tokenResp = await fetch('/bff-api/user-marketing/secure-token', {
                credentials: 'include',
            });
            if (!tokenResp.ok) return;

            const { secureToken } = await tokenResp.json();
            if (!secureToken) return;

            const formData = new FormData();
            formData.append('optInMarketing', String(optIn));
            formData.append('secureToken', secureToken);

            await fetch(`${sellerUrl}/manage-account/subscribe`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'x-requested-with': 'XMLHttpRequest' },
                body: formData,
                redirect: 'manual',
            });
        } catch {
            // Silently fail
        } finally {
            setIsSubmitting(false);
            handleClose();
        }
    }, [optIn, isSubmitting, handleClose, sellerUrl]);

    if (!isOpen) return null;

    return (
        <Overlay data-testid="marketing-consent-modal">
            <ModalContainer>
                <CloseButton
                    type="button"
                    onClick={handleClose}
                    aria-label="Close"
                    data-testid="marketing-consent-close"
                >
                    &#x2715;
                </CloseButton>

                <IllustrationWrapper>
                    <img src={receiveEmailsImg} alt="Receive emails" width={128} height={140} />
                </IllustrationWrapper>

                <ModalTitle>Let&apos;s keep in touch</ModalTitle>

                <CheckboxWrapper>
                    <HiddenCheckbox
                        type="checkbox"
                        checked={optIn}
                        onChange={(e) => setOptIn(e.target.checked)}
                        data-testid="marketing-consent-checkbox"
                    />
                    <CustomCheckbox checked={optIn} aria-hidden="true" />
                    <CheckboxLabel>
                        I want to receive updates, tips and offers from Gumtree and partners.
                    </CheckboxLabel>
                </CheckboxWrapper>

                <ConfirmButton
                    type="button"
                    onClick={handleConfirm}
                    disabled={isSubmitting}
                    data-testid="marketing-consent-confirm"
                >
                    {isMobile ? 'Continue' : 'Confirm'}
                </ConfirmButton>
            </ModalContainer>
        </Overlay>
    );
};

export default MarketingConsentModal;
